import { type FC } from 'react';

import { theme } from '../../theme';
import Helper from '../Helper';
import type { HelperState } from '../Helper/types';
import Label from '../Label';
import Typography from '../Typography';
import {
  StyledBarWrapper,
  StyledContainer,
  StyledHorizontalRow,
  StyledIndicator,
  StyledLinearRoot,
  StyledTrack,
} from './Progress.styled';
import type { ProgressProps } from './types';

function getHelperState(color: NonNullable<ProgressProps['color']>): HelperState {
  if (color === 'brand' || color === 'success') return 'success';
  if (color === 'error') return 'destructive';
  return 'default';
}

export const Progress: FC<ProgressProps> = ({
  value,
  min = 0,
  max = 100,
  size = 'md',
  color = 'brand',
  shimmering = false,
  direction = 'vertical',
  label,
  progressLabel,
  showProgressLabel,
  helperText,
  className,
}) => {
  const percentage =
    value !== null && value !== undefined ? ((value - min) / (max - min)) * 100 : undefined;

  const resolvedProgressLabel =
    progressLabel ?? (percentage !== undefined ? `${Math.round(percentage)}%` : undefined);

  const shouldShowPercentage = (showProgressLabel ?? Boolean(label)) && !!resolvedProgressLabel;

  const percentageNode = shouldShowPercentage ? (
    <Typography as="span" variant="body3" color={theme.colors.text.primary} noWrap>
      {resolvedProgressLabel}
    </Typography>
  ) : null;

  const bar = (
    <StyledLinearRoot value={value ?? null} min={min} max={max}>
      <StyledTrack $size={size}>
        <StyledIndicator
          $color={color}
          $shimmering={shimmering}
          style={percentage !== undefined ? { width: `${percentage}%` } : undefined}
        />
      </StyledTrack>
    </StyledLinearRoot>
  );

  if (direction === 'horizontal') {
    return (
      <StyledContainer className={className} $direction="horizontal">
        <StyledHorizontalRow>
          {label && (
            <Typography as="span" variant="body3" color={theme.colors.text.primary} noWrap>
              {label}
            </Typography>
          )}
          <StyledBarWrapper>{bar}</StyledBarWrapper>
          {percentageNode}
        </StyledHorizontalRow>
      </StyledContainer>
    );
  }

  return (
    <StyledContainer className={className} $direction="vertical">
      {(label || percentageNode) && <Label topRightLabel={percentageNode}>{label}</Label>}
      {bar}
      {helperText && <Helper label={helperText} state={getHelperState(color)} />}
    </StyledContainer>
  );
};

Progress.displayName = 'Progress';
